import React from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import i18n from 'i18next';
import { Trans } from 'react-i18next';

import HealthIconSvg from 'common/images/HealthIconSvg';
import { Task, Wrench } from 'common/images/FaIcons';
import NoteSvgNew from 'common/images/NoteSvgNew';
import { history } from '../../../common/helpers/history';
import Text_T from '../../../common/typography/Text/Text';
import AlarmFlagSvg from '../../../common/images/AlarmFlagSvg';
import OverallAlarmWarningNew from '../../../common/images/OverallAlarmWarningNew';
import OverallAlarmCriticalNew from '../../../common/images/OverallAlarmCriticalNew';
import MoteUnreachableNew from '../../../common/images/MoteUnreachableNew';
import MachineSvg from '../../../common/images/MachineSvg';
import NewBatteryIcon from '../../../common/images/NewBatteryIcon';
import { humanize } from '../../../common/helpers/humanize';
import {
  getActivitySensorTypeLabel,
  getMoteUnreachableTitleText,
  getTransmitterUnreachableTitleText
} from '../helpers/activityEventDisplay';
import { alarmLevel, overallMeasureLabel } from '../helpers/overallAlarmActivity';

import colors from '../../../common/styles/colors';

export const Text = styled(Text_T)`
  font-size: 13px;
  ${props => props.hover && `
    &:hover {
      color: ${props.theme.primaryColor};
      cursor: pointer;
    }
  `}
`;

const onSensorClick = (e, serial_number) => {
  e.stopPropagation();
  history.push('/devices', { serial_number });
};

const onMachineClick = (e, machine_id) => {
  e.stopPropagation();
  history.push(`/machines/${machine_id}/overview`);
};

const DescriptionContainer = styled.div`
${props => props.marginTop && 'margin-top: 0.5em;'}
  > ${Text} {
    margin-bottom: 0.5em;
  }
`;

const getSpeedRangeText = (threshold_value) => {
  if (!threshold_value) return '';
  return i18n.t('activity.activityFormat.speedRange', {
    defaultValue: 'for speed range - {{thresholdValue}}',
    thresholdValue: threshold_value
  });
};

const getClosedEventText = closedAt => (
  closedAt ? (
    <Text>
      {i18n.t('activity.activityFormat.closedOn', {
        defaultValue: 'This event was closed on {{closedAt}}.',
        closedAt
      })}
    </Text>
  ) : null
);

const getTriggeredSpeedRangesText = (triggeredThresholdTypes, thresholdType) => {
  if (!triggeredThresholdTypes || triggeredThresholdTypes.length <= 1) return null;
  const ranges = triggeredThresholdTypes.filter(t => t !== thresholdType).join(', ');
  return (
    <Text>
      {i18n.t('activity.activityFormat.triggeredOnSpeedRanges', {
        defaultValue: 'Also triggered on speed ranges - {{ranges}}',
        ranges
      })}
    </Text>
  );
};

const getFeedbackRaisedSpeedRangesText = speedInfo => (
  !_.isEmpty(speedInfo) ? (
    <Text>
      {i18n.t('activity.activityFormat.feedbackRaisedSpeedRanges', {
        defaultValue: 'Feedback raised for speed ranges - {{ranges}}.',
        ranges: speedInfo.filter(info => info.speed_bin).map(info => info.speed_bin).join(', ')
      })}
    </Text>
  ) : null
);


const activityFormat = {
  vibration_warning: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>{i18n.t('activity.activityFormat.titles.machineHasA', { defaultValue: ' machine has a ' })}<Text bold>{i18n.t('activity.activityFormat.titles.vibrationWarningAlarm', { defaultValue: 'vibration warning alarm' })}</Text>{i18n.t('activity.activityFormat.titles.onThe', { defaultValue: ' on the ' })}<Text bold>{humanize(activity.location)}</Text>{i18n.t('activity.activityFormat.titles.at', { defaultValue: ' at ' })}<Text bold>{activity.component_name}</Text>{i18n.t('activity.activityFormat.titles.in', { defaultValue: ' in ' })}<Text bold>{activity.direction}</Text>{i18n.t('activity.activityFormat.titles.direction', { defaultValue: ' direction' })}</Text>,
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>
            {i18n.t('activity.groupedActivity.messages.vibrationsExceededWarning', {
              defaultValue: 'Vibrations have exceeded warning alarm on the {{location}} at {{componentName}} in {{direction}} direction.',
              location: humanize(activity.location),
              componentName: activity.component_name,
              direction: activity.direction
            })}
          </Text>
          <Text>
            {i18n.t('activity.groupedActivity.messages.latestLevelRmsExceededThreshold', {
              defaultValue: 'The latest {{alarmLevelPrefix}} RMS of {{rms}} {{units}} exceeds the threshold of {{thresholdValue}} {{units}} by {{percentIncrease}}% {{speedRange}}.',
              alarmLevelPrefix: '',
              rms: activity.rms,
              units: activity.units,
              thresholdValue: activity.threshold_value,
              percentIncrease: activity.percent_increase,
              speedRange: getSpeedRangeText(activity.threshold_type)
            })}
          </Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
        <DescriptionContainer marginTop>
          {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmWarningNew />
  },
  vibration_critical: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>{i18n.t('activity.activityFormat.titles.machineHasA', { defaultValue: ' machine has a ' })}<Text bold>{i18n.t('activity.activityFormat.titles.vibrationCriticalAlarm', { defaultValue: 'vibration critical alarm' })}</Text>{i18n.t('activity.activityFormat.titles.onThe', { defaultValue: ' on the ' })}<Text bold>{humanize(activity.location)}</Text>{i18n.t('activity.activityFormat.titles.at', { defaultValue: ' at ' })}<Text bold>{activity.component_name}</Text>{i18n.t('activity.activityFormat.titles.in', { defaultValue: ' in ' })}<Text bold>{activity.direction}</Text>{i18n.t('activity.activityFormat.titles.direction', { defaultValue: ' direction' })}</Text>,
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>
            {i18n.t('activity.groupedActivity.messages.vibrationsExceededCritical', {
              defaultValue: 'Vibrations have exceeded critical alarm on the {{location}} at {{componentName}} in {{direction}} direction.',
              location: humanize(activity.location),
              componentName: activity.component_name,
              direction: activity.direction
            })}
          </Text>
          <Text>
            {i18n.t('activity.groupedActivity.messages.latestLevelRmsExceededThreshold', {
              defaultValue: 'The latest {{alarmLevelPrefix}} RMS of {{rms}} {{units}} exceeds the threshold of {{thresholdValue}} {{units}} by {{percentIncrease}}% {{speedRange}}.',
              alarmLevelPrefix: '',
              rms: activity.rms,
              units: activity.units,
              thresholdValue: activity.threshold_value,
              percentIncrease: activity.percent_increase,
              speedRange: getSpeedRangeText(activity.threshold_type)
            })}
          </Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
        <DescriptionContainer marginTop>
          {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmCriticalNew />
  },
  vibration_demod_warning: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>{i18n.t('activity.activityFormat.titles.machineHasA', { defaultValue: ' machine has a ' })}<Text bold>{i18n.t('activity.activityFormat.titles.demodVibrationWarningAlarm', { defaultValue: 'demod vibration warning alarm' })}</Text>{i18n.t('activity.activityFormat.titles.onThe', { defaultValue: ' on the ' })}<Text bold>{humanize(activity.location)}</Text>{i18n.t('activity.activityFormat.titles.at', { defaultValue: ' at ' })}<Text bold>{activity.component_name}</Text>{i18n.t('activity.activityFormat.titles.in', { defaultValue: ' in ' })}<Text bold>{activity.direction}</Text>{i18n.t('activity.activityFormat.titles.direction', { defaultValue: ' direction' })}</Text>,
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>
            {i18n.t('activity.groupedActivity.messages.vibrationsExceededLevel', {
              defaultValue: 'Vibrations have exceeded {{alarmLevelPrefix}} {{alarmLevel}} alarm on the {{location}} at {{componentName}} in {{direction}} direction.',
              alarmLevelPrefix: activity.alarm_level_prefix,
              alarmLevel: activity.alarm_level,
              location: humanize(activity.location),
              componentName: activity.component_name,
              direction: activity.direction
            })}
          </Text>
          <Text>
            {i18n.t('activity.groupedActivity.messages.latestLevelRmsExceededThreshold', {
              defaultValue: 'The latest {{alarmLevelPrefix}} RMS of {{rms}} {{units}} exceeds the threshold of {{thresholdValue}} {{units}} by {{percentIncrease}}% {{speedRange}}.',
              alarmLevelPrefix: activity.alarm_level_prefix,
              rms: activity.rms,
              units: activity.units,
              thresholdValue: activity.threshold_value,
              percentIncrease: activity.percent_increase,
              speedRange: getSpeedRangeText(activity.threshold_type)
            })}
          </Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
        <DescriptionContainer marginTop>
          {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmWarningNew />
  },
  vibration_demod_critical: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>{i18n.t('activity.activityFormat.titles.machineHasA', { defaultValue: ' machine has a ' })}<Text bold>{i18n.t('activity.activityFormat.titles.demodVibrationCriticalAlarm', { defaultValue: 'demod vibration critical alarm' })}</Text>{i18n.t('activity.activityFormat.titles.onThe', { defaultValue: ' on the ' })}<Text bold>{humanize(activity.location)}</Text>{i18n.t('activity.activityFormat.titles.at', { defaultValue: ' at ' })}<Text bold>{activity.component_name}</Text>{i18n.t('activity.activityFormat.titles.in', { defaultValue: ' in ' })}<Text bold>{activity.direction}</Text>{i18n.t('activity.activityFormat.titles.direction', { defaultValue: ' direction' })}</Text>,
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>
            {i18n.t('activity.groupedActivity.messages.vibrationsExceededLevel', {
              defaultValue: 'Vibrations have exceeded {{alarmLevelPrefix}} {{alarmLevel}} alarm on the {{location}} at {{componentName}} in {{direction}} direction.',
              alarmLevelPrefix: activity.alarm_level_prefix,
              alarmLevel: activity.alarm_level,
              location: humanize(activity.location),
              componentName: activity.component_name,
              direction: activity.direction
            })}
          </Text>
          <Text>
            {i18n.t('activity.groupedActivity.messages.latestLevelRmsExceededThreshold', {
              defaultValue: 'The latest {{alarmLevelPrefix}} RMS of {{rms}} {{units}} exceeds the threshold of {{thresholdValue}} {{units}} by {{percentIncrease}}% {{speedRange}}.',
              alarmLevelPrefix: activity.alarm_level_prefix,
              rms: activity.rms,
              units: activity.units,
              thresholdValue: activity.threshold_value,
              percentIncrease: activity.percent_increase,
              speedRange: getSpeedRangeText(activity.threshold_type)
            })}
          </Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
        <DescriptionContainer marginTop>
          {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmCriticalNew />
  },
  vibration_acceleration_warning: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>{i18n.t('activity.activityFormat.titles.machineHasA', { defaultValue: ' machine has a ' })}<Text bold>{i18n.t('activity.activityFormat.titles.vibrationAccelerationWarningAlarm', { defaultValue: 'vibration acceleration warning alarm' })}</Text>{i18n.t('activity.activityFormat.titles.onThe', { defaultValue: ' on the ' })}<Text bold>{humanize(activity.location)}</Text>{i18n.t('activity.activityFormat.titles.at', { defaultValue: ' at ' })}<Text bold>{activity.component_name}</Text>{i18n.t('activity.activityFormat.titles.in', { defaultValue: ' in ' })}<Text bold>{activity.direction}</Text>{i18n.t('activity.activityFormat.titles.direction', { defaultValue: ' direction' })}</Text>,
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>
            {i18n.t('activity.groupedActivity.messages.vibrationsExceededLevel', {
              defaultValue: 'Vibrations have exceeded {{alarmLevelPrefix}} {{alarmLevel}} alarm on the {{location}} at {{componentName}} in {{direction}} direction.',
              alarmLevelPrefix: activity.alarm_level_prefix,
              alarmLevel: activity.alarm_level,
              location: humanize(activity.location),
              componentName: activity.component_name,
              direction: activity.direction
            })}
          </Text>
          <Text>
            {i18n.t('activity.groupedActivity.messages.latestLevelRmsExceededThreshold', {
              defaultValue: 'The latest {{alarmLevelPrefix}} RMS of {{rms}} {{units}} exceeds the threshold of {{thresholdValue}} {{units}} by {{percentIncrease}}% {{speedRange}}.',
              alarmLevelPrefix: activity.alarm_level_prefix,
              rms: activity.rms,
              units: activity.units,
              thresholdValue: activity.threshold_value,
              percentIncrease: activity.percent_increase,
              speedRange: getSpeedRangeText(activity.threshold_type)
            })}
          </Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
        <DescriptionContainer marginTop>
          {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmWarningNew />
  },
  vibration_acceleration_critical: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>{i18n.t('activity.activityFormat.titles.machineHasA', { defaultValue: ' machine has a ' })}<Text bold>{i18n.t('activity.activityFormat.titles.vibrationAccelerationCriticalAlarm', { defaultValue: 'vibration acceleration critical alarm' })}</Text>{i18n.t('activity.activityFormat.titles.onThe', { defaultValue: ' on the ' })}<Text bold>{humanize(activity.location)}</Text>{i18n.t('activity.activityFormat.titles.at', { defaultValue: ' at ' })}<Text bold>{activity.component_name}</Text>{i18n.t('activity.activityFormat.titles.in', { defaultValue: ' in ' })}<Text bold>{activity.direction}</Text>{i18n.t('activity.activityFormat.titles.direction', { defaultValue: ' direction' })}</Text>,
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>
            {i18n.t('activity.groupedActivity.messages.vibrationsExceededLevel', {
              defaultValue: 'Vibrations have exceeded {{alarmLevelPrefix}} {{alarmLevel}} alarm on the {{location}} at {{componentName}} in {{direction}} direction.',
              alarmLevelPrefix: activity.alarm_level_prefix,
              alarmLevel: activity.alarm_level,
              location: humanize(activity.location),
              componentName: activity.component_name,
              direction: activity.direction
            })}
          </Text>
          <Text>
            {i18n.t('activity.groupedActivity.messages.latestLevelRmsExceededThreshold', {
              defaultValue: 'The latest {{alarmLevelPrefix}} RMS of {{rms}} {{units}} exceeds the threshold of {{thresholdValue}} {{units}} by {{percentIncrease}}% {{speedRange}}.',
              alarmLevelPrefix: activity.alarm_level_prefix,
              rms: activity.rms,
              units: activity.units,
              thresholdValue: activity.threshold_value,
              percentIncrease: activity.percent_increase,
              speedRange: getSpeedRangeText(activity.threshold_type)
            })}
          </Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
        <DescriptionContainer marginTop>
          {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmCriticalNew />
  },
  vibration_envelope_alarm: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>{i18n.t('activity.activityFormat.titles.machineHasA', { defaultValue: ' machine has a ' })}<Text bold>{i18n.t('activity.activityFormat.titles.vibrationEnvelopeAlarm', { defaultValue: 'vibration envelope alarm' })}</Text>{i18n.t('activity.activityFormat.titles.onThe', { defaultValue: ' on the ' })}<Text bold>{humanize(activity.location)}</Text>{i18n.t('activity.activityFormat.titles.at', { defaultValue: ' at ' })}<Text bold>{activity.component_name}</Text>{i18n.t('activity.activityFormat.titles.in', { defaultValue: ' in ' })}<Text bold>{activity.direction}</Text>{i18n.t('activity.activityFormat.titles.direction', { defaultValue: ' direction' })}</Text>,
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>
            {i18n.t('activity.groupedActivity.messages.vibrationSpectrumExceededEnvelopeLimit', {
              defaultValue: 'Vibration spectrum exceeded envelope limit {{speedRange}}.',
              speedRange: getSpeedRangeText(activity.threshold_type)
            })}
          </Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
        <DescriptionContainer marginTop>
          {activity.triggered_threshold_types && activity.triggered_threshold_types.length > 1 && (
            <Text>
              {i18n.t('activity.activityFormat.triggeredOnSpeedRanges', {
                defaultValue: 'Also triggered on speed ranges - {{ranges}}',
                ranges: activity.triggered_threshold_types.filter(t => t !== activity.threshold_type).join(', ')
              })}
            </Text>
          )}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmCriticalNew />
  },
  current_envelope_alarm: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>{i18n.t('activity.activityFormat.titles.machineHasA', { defaultValue: ' machine has a ' })}<Text bold>{i18n.t('activity.activityFormat.titles.currentEnvelopeAlarm', { defaultValue: 'current envelope alarm' })}</Text>{i18n.t('activity.activityFormat.titles.onThe', { defaultValue: ' on the ' })}<Text bold>{humanize(activity.location)}</Text>{i18n.t('activity.activityFormat.titles.at', { defaultValue: ' at ' })}<Text bold>{activity.component_name}</Text></Text>,
    description: activity => (
      <>
        <DescriptionContainer>
          <Text>{i18n.t('activity.activityFormat.currentSpectrumExceededEnvelope', { defaultValue: 'Current spectrum exceeded envelope limit.' })}</Text>
          {getClosedEventText(activity.closed_at)}
        </DescriptionContainer>
      </>
    ),
    icon: () => <OverallAlarmCriticalNew />
  },
  machine_condition_warning: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>{i18n.t('activity.activityFormat.titles.isIn', { defaultValue: ' is in ' })}<Text bold>{i18n.t('activity.activityFormat.titles.warning', { defaultValue: 'warning' })}</Text>{i18n.t('activity.activityFormat.titles.condition', { defaultValue: ' condition' })}</Text>,
    description: activity => (
      <DescriptionContainer>
        <Text>
          {i18n.t('activity.groupedActivity.messages.machineConditionWarningAt', {
            defaultValue: 'This machine condition has changed to warning at {{createdAt}}.',
            createdAt: activity.created_at
          })}
        </Text>
        {getClosedEventText(activity.closed_at)}
      </DescriptionContainer>
    ),
    icon: () => <MachineSvg fill={colors.warning} />
  },
  machine_condition_critical: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>{i18n.t('activity.activityFormat.titles.isIn', { defaultValue: ' is in ' })}<Text bold>{i18n.t('activity.activityFormat.titles.critical', { defaultValue: 'critical' })}</Text>{i18n.t('activity.activityFormat.titles.condition', { defaultValue: ' condition' })}</Text>,
    description: activity => (
      <DescriptionContainer>
        <Text>
          {i18n.t('activity.groupedActivity.messages.machineConditionCriticalAt', {
            defaultValue: 'This machine condition has changed to critical at {{createdAt}}.',
            createdAt: activity.created_at
          })}
        </Text>
        {getClosedEventText(activity.closed_at)}
      </DescriptionContainer>
    ),
    icon: () => <MachineSvg fill={colors.critical} />
  },
  health_score_warning: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>{i18n.t('activity.activityFormat.titles.hasA', { defaultValue: ' has a ' })}<Text bold>{i18n.t('activity.activityFormat.titles.healthScoreWarning', { defaultValue: 'health score warning' })}</Text>{i18n.t('activity.activityFormat.titles.alarm', { defaultValue: ' alarm' })}</Text>,
    description: activity => (
      <DescriptionContainer>
        <Text>
          {i18n.t('activity.activityFormat.healthScore.changedTo', {
            defaultValue: 'The health score {{changeDirection}} to {{value}} which is {{comparison}}.',
            changeDirection: activity.change_direction,
            value: activity.parameter_value,
            comparison: activity.comparision_string
          })}
        </Text>
        {getClosedEventText(activity.closed_at)}
      </DescriptionContainer>
    ),
    icon: () => <AlarmFlagSvg fill={colors.alarmWarning} />
  },
  health_score_critical: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>{i18n.t('activity.activityFormat.titles.hasA', { defaultValue: ' has a ' })}<Text bold>{i18n.t('activity.activityFormat.titles.healthScoreCritical', { defaultValue: 'health score critical' })}</Text>{i18n.t('activity.activityFormat.titles.alarm', { defaultValue: ' alarm' })}</Text>,
    description: activity => (
      <DescriptionContainer>
        <Text>
          {i18n.t('activity.activityFormat.healthScore.changedTo', {
            defaultValue: 'The health score {{changeDirection}} to {{value}} which is {{comparison}}.',
            changeDirection: activity.change_direction,
            value: activity.parameter_value,
            comparison: activity.comparision_string
          })}
        </Text>
        {getClosedEventText(activity.closed_at)}
      </DescriptionContainer>
    ),
    icon: () => <AlarmFlagSvg fill={colors.alarmCritical} />
  },
  battery_level_warning: {
    title: activity => (
      <Text>
        <Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>
        {' '}
        {getActivitySensorTypeLabel(i18n.t.bind(i18n), activity.sensor_serial_number)}
        {' '}
        <Text bold hover onClick={e => onSensorClick(e, activity.sensor_serial_number)}>{activity.sensor_serial_number}</Text>
        {i18n.t('activity.activityFormat.titles.hasA', { defaultValue: ' has a ' })}
        <Text bold>{i18n.t('activity.groupedActivity.battery.warningTitle', { defaultValue: 'Battery level warning alarm' })}</Text>
      </Text>
    ),
    description: activity => (
      <DescriptionContainer>
        <Text>
          {i18n.t('activity.activityFormat.battery.moteLessThanRemaining', {
            defaultValue: 'The mote on {{location}} has less than {{batteryLevel}} battery remaining.',
            location: humanize(activity.location),
            batteryLevel: activity.battery_level
          })}
        </Text>
        {getClosedEventText(activity.closed_at)}
      </DescriptionContainer>
    ),
    icon: () => <NewBatteryIcon fill="#F0C000" />
  },
  battery_level_critical: {
    title: activity => (
      <Text>
        <Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>
        {' '}
        {getActivitySensorTypeLabel(i18n.t.bind(i18n), activity.sensor_serial_number)}
        {' '}
        <Text bold hover onClick={e => onSensorClick(e, activity.sensor_serial_number)}>{activity.sensor_serial_number}</Text>
        {i18n.t('activity.activityFormat.titles.hasA', { defaultValue: ' has a ' })}
        <Text bold>{i18n.t('activity.groupedActivity.titles.batteryLevelCritical', { defaultValue: '{{serialNumber}} has a battery level critical alarm', serialNumber: activity.sensor_serial_number })}</Text>
      </Text>
    ),
    description: activity => (
      <DescriptionContainer>
        <Text>
          {i18n.t('activity.activityFormat.battery.moteLessThanRemaining', {
            defaultValue: 'The mote on {{location}} has less than {{batteryLevel}} battery remaining.',
            location: humanize(activity.location),
            batteryLevel: activity.battery_level
          })}
        </Text>
        {getClosedEventText(activity.closed_at)}
      </DescriptionContainer>
    ),
    icon: () => <NewBatteryIcon fill="#FF4F39" />
  },
  tx_battery_level_warning: {
    title: activity => (
      <Text>
        <Text bold hover onClick={e => onSensorClick(e, activity.sensor_serial_number)}>{activity.sensor_serial_number}</Text>
        {i18n.t('activity.activityFormat.titles.hasA', { defaultValue: ' has a ' })}
        <Text bold>{i18n.t('activity.groupedActivity.titles.txBatteryLevelWarning', { defaultValue: '{{serialNumber}} has a tx battery level warning alarm', serialNumber: activity.sensor_serial_number })}</Text>
      </Text>
    ),
    description: activity => (
      <DescriptionContainer>
        <Text>
          {i18n.t('activity.activityFormat.battery.transmitterLessThanRemaining', {
            defaultValue: 'This transmitter has less than {{batteryLevel}} battery remaining.',
            batteryLevel: activity.battery_level
          })}
        </Text>
        {activity.closed_at && (
          <Text>
            {i18n.t('activity.groupedActivity.closedOn', {
              defaultValue: 'This event was closed on {{closedAt}}.',
              closedAt: activity.closed_at
            })}
          </Text>
        )}
      </DescriptionContainer>
    ),
    icon: () => <NewBatteryIcon fill="#F0C000" />
  },
  tx_battery_level_critical: {
    title: activity => (
      <Text>
        <Text bold hover onClick={e => onSensorClick(e, activity.sensor_serial_number)}>{activity.sensor_serial_number}</Text>
        {i18n.t('activity.activityFormat.titles.hasA', { defaultValue: ' has a ' })}
        <Text bold>{i18n.t('activity.groupedActivity.titles.txBatteryLevelCritical', { defaultValue: '{{serialNumber}} has a battery level critical alarm', serialNumber: activity.sensor_serial_number })}</Text>
      </Text>
    ),
    description: activity => (
      <DescriptionContainer>
        <Text>
          {i18n.t('activity.activityFormat.battery.transmitterLessThanRemaining', {
            defaultValue: 'This transmitter has less than {{batteryLevel}} battery remaining.',
            batteryLevel: activity.battery_level
          })}
        </Text>
        {activity.closed_at && (
          <Text>
            {i18n.t('activity.groupedActivity.closedOn', {
              defaultValue: 'This event was closed on {{closedAt}}.',
              closedAt: activity.closed_at
            })}
          </Text>
        )}
      </DescriptionContainer>
    ),
    icon: () => <NewBatteryIcon fill="#FF4F39" />
  },
  mote_unreachable: {
    title: activity => (
      <Text>
        <Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>
        {' '}
        {getMoteUnreachableTitleText(i18n.t.bind(i18n), activity)}
      </Text>
    ),
    description: activity => (
      <DescriptionContainer>
        {!activity.closed_at && (
          <>
            <Text>
              {i18n.t('activity.groupedActivity.messages.moteUnreachableSince', {
                defaultValue: 'This mote on {{location}} is unreachable since {{duration}}.',
                location: humanize(activity.location),
                duration: activity.duration
              })}
            </Text>
            <Text>
              {i18n.t('activity.groupedActivity.messages.sentLastReadingAt', {
                defaultValue: 'It sent last reading at {{time}}.',
                time: activity.last_reporting_time
              })}
            </Text>
          </>
        )}

        {activity.closed_at && (
          <Text>
            {i18n.t('activity.groupedActivity.messages.moteUnreachableBetween', {
              defaultValue: 'This mote on {{location}} was unreachable for {{duration}} between {{startTime}} and {{endTime}}.',
              location: humanize(activity.location),
              duration: activity.duration,
              startTime: activity.last_reporting_time,
              endTime: activity.closed_at
            })}
          </Text>
        )}
      </DescriptionContainer>
    ),
    icon: () => <MoteUnreachableNew fill="red" />
  },
  tx_unreachable: {
    title: activity => (
      <Text>
        <Text bold hover onClick={e => onSensorClick(e, activity.sensor_serial_number)}>{activity.sensor_serial_number}</Text>
        {' '}
        {getTransmitterUnreachableTitleText(i18n.t.bind(i18n), activity)}
      </Text>
    ),
    description: activity => (
      <DescriptionContainer>
        {!activity.closed_at && (
          <>
            <Text>
              {i18n.t('activity.groupedActivity.messages.transmitterUnreachableSince', {
                defaultValue: 'This transmitter is unreachable since {{duration}}.',
                duration: activity.duration
              })}
            </Text>
            <Text>
              {i18n.t('activity.groupedActivity.messages.lastReportedAt', {
                defaultValue: 'It last reported at {{time}}.',
                time: activity.last_reporting_time
              })}
            </Text>
          </>
        )}
        {activity.closed_at && (
          <Text>
            {i18n.t('activity.groupedActivity.messages.transmitterUnreachableBetween', {
              defaultValue: 'This transmitter was unreachable for {{duration}} between {{startTime}} and {{endTime}}.',
              duration: activity.duration,
              startTime: activity.last_reporting_time,
              endTime: activity.closed_at
            })}
          </Text>
        )}
      </DescriptionContainer>
    ),
    icon: () => <AlarmFlagSvg fill={colors.alarmCritical} />
  },
  // Any other overall alarm, e.g. one an admin created. Vibration measures send
  // the rms-shaped description; every other tag type sends the custom shape.
  overall_alarm: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>{i18n.t('activity.activityFormat.titles.machineHasA', { defaultValue: ' machine has a ' })}<Text bold>{i18n.t('activity.activityFormat.titles.measureLevelAlarm', { defaultValue: '{{measure}} {{alarmLevel}} alarm', measure: overallMeasureLabel(activity), alarmLevel: i18n.t(`activity.activityFormat.alarmLevels.${alarmLevel(activity)}`, { defaultValue: alarmLevel(activity) }) })}</Text>{activity.location && <>{i18n.t('activity.activityFormat.titles.onThe', { defaultValue: ' on the ' })}<Text bold>{humanize(activity.location)}</Text></>}{activity.component_name && <>{i18n.t('activity.activityFormat.titles.at', { defaultValue: ' at ' })}<Text bold>{activity.component_name}</Text></>}{activity.direction && <>{i18n.t('activity.activityFormat.titles.in', { defaultValue: ' in ' })}<Text bold>{activity.direction}</Text>{i18n.t('activity.activityFormat.titles.direction', { defaultValue: ' direction' })}</>}</Text>,
    description: (activity) => {
      if (activity.rms === undefined) return activityFormat.custom.description(activity);

      return (
        <>
          <DescriptionContainer>
            <Text>
              {i18n.t('activity.groupedActivity.messages.latestMeasureExceededThreshold', {
                defaultValue: 'The latest {{measure}} of {{value}} {{units}} exceeds the threshold of {{thresholdValue}} {{units}} by {{percentIncrease}}% {{speedRange}}.',
                measure: activity.measure,
                value: activity.rms,
                units: activity.units,
                thresholdValue: activity.threshold_value,
                percentIncrease: activity.percent_increase,
                speedRange: getSpeedRangeText(activity.threshold_type)
              })}
            </Text>
            {getClosedEventText(activity.closed_at)}
          </DescriptionContainer>
          <DescriptionContainer marginTop>
            {getTriggeredSpeedRangesText(activity.triggered_threshold_types, activity.threshold_type)}
          </DescriptionContainer>
        </>
      );
    },
    icon: activity => (alarmLevel(activity) === 'critical' ? <OverallAlarmCriticalNew /> : <OverallAlarmWarningNew />)
  },
  custom: {
    title: activity => (
<>
    <Text>
      <Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>
      {i18n.t('activity.activityFormat.titles.hasA', { defaultValue: ' has a ' })}
      <Text bold>{humanize(activity.event_type)}</Text>
      {i18n.t('activity.activityFormat.titles.alarm', { defaultValue: ' alarm' })}
      {activity.location && (
        <Text>
          {i18n.t('activity.activityFormat.titles.onThe', { defaultValue: ' on the ' })}
          <Text bold>{humanize(activity.location)}</Text>
        </Text>
      )}
      {activity.direction && (
        <Text>
          {i18n.t('activity.activityFormat.titles.in', { defaultValue: ' in ' })}
          <Text bold>{activity.direction}</Text>
          {i18n.t('activity.activityFormat.titles.direction', { defaultValue: ' direction' })}
        </Text>
      )}
    </Text>
</>
    ),
    description: activity => (
      <>
      {activity.parameter_type !== 'health_score' ? (
        <DescriptionContainer>
          {activity.location && (
            <Text>
              {i18n.t('activity.activityFormat.custom.parameterChangedAtLocation', {
                defaultValue: 'The {{parameterType}} on {{location}} {{changeDirection}} to {{value}} which is {{comparison}}.',
                parameterType: activity.parameter_type,
                location: humanize(activity.location),
                changeDirection: activity.change_direction,
                value: activity.parameter_value,
                comparison: activity.comparision_string,
              })}
            </Text>
          )}
          {!activity.location && (
            <Text>
              {i18n.t('activity.activityFormat.custom.parameterChangedNoLocation', {
                defaultValue: 'The {{parameterType}} {{changeDirection}} to {{value}} which is {{comparison}}.',
                parameterType: activity.parameter_type,
                changeDirection: activity.change_direction,
                value: activity.parameter_value,
                comparison: activity.comparision_string,
              })}
            </Text>
          )}
          {activity.closed_at && (
            <Text>
              {i18n.t('activity.activityFormat.closedOn', {
                defaultValue: 'This event was closed on {{closedAt}}.',
                closedAt: activity.closed_at
              })}
            </Text>
          )}
        </DescriptionContainer>
      ) : (
        <DescriptionContainer>
          <Text>
            {i18n.t('activity.activityFormat.healthScore.changedTo', {
              defaultValue: 'The health score {{changeDirection}} to {{value}} which is {{comparison}}.',
              changeDirection: activity.change_direction,
              value: activity.parameter_value,
              comparison: activity.comparision_string,
            })}
          </Text>
          {activity.closed_at && (
            <Text>
              {i18n.t('activity.activityFormat.closedOn', {
                defaultValue: 'This event was closed on {{closedAt}}.',
                closedAt: activity.closed_at
              })}
            </Text>
          )}
        </DescriptionContainer>
      )}
      </>
    ),
    icon: activity => <AlarmFlagSvg fill={activity.event_type && activity.event_type.includes('critical') ? colors.alarmCritical : colors.alarmWarning} />
  },
  diagnosis: {
    title: activity => (
<>
    <Text>
      <Text bold>{activity.rule_name}</Text>
      {' '}
      {i18n.t('activity.activityFormat.diagnosis.triggeredOn', { defaultValue: 'triggered on' })}
      {' '}
      <Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>
      {activity.location && (
        <Text>
          {i18n.t('activity.activityFormat.titles.at', { defaultValue: ' at ' })}
          <Text bold>{humanize(activity.location)}</Text>
        </Text>
      )}
    </Text>
</>
    ),
    description: activity => (
      <DescriptionContainer>
        <Text>{activity.recommendation}</Text>
      </DescriptionContainer>
    ),
    icon: () => <HealthIconSvg />
  },
  new_operation_modes: {
    title: activity => (
      <Text>
        {i18n.t('activity.activityFormat.newOperationModeDetectedOn', { defaultValue: 'New operating mode detected on' })}
        {' '}
        <Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text>
      </Text>
    ),
    description: () => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.activityFormat.newOperatingModeDescription', { defaultValue: 'Re-configure VFD speed ranges.' })}</Text>
      </DescriptionContainer>
    ),
    icon: () => ''
  },
  baseline_missing_for_operation_modes: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text> has baselines missing for few operating modes at <Text bold>{activity.component_name}</Text></Text>,
    description: activity => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.activityFormat.reBaselineIncludeOperationModes', { defaultValue: 'Re-baseline to include these operation modes.' })}</Text>
        {getFeedbackRaisedSpeedRangesText(activity.speed_info)}
      </DescriptionContainer>
    ),
    icon: () => ''
  },
  baseline_higher_than_measurement_value: {
    title: activity => <Text><Text bold hover onClick={e => onMachineClick(e, activity.machine_id)}>{activity.machine_name}</Text> vibrations changed from earlier indicated normal behavior at <Text bold>{activity.component_name}</Text></Text>,
    description: activity => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.activityFormat.reBaselineAssetRecentData', { defaultValue: 'Re-baseline asset with recent data.' })}</Text>
        {getFeedbackRaisedSpeedRangesText(activity.speed_info)}
      </DescriptionContainer>
    ),
    icon: () => ''
  },
  health_score_model_retraining_required: {
    title: activity => (
      <Text>
        <Trans
          i18nKey="activity.activityFormat.healthScorePausedTitle"
          defaults="Health score paused on <machine>{{machineName}}</machine> at <component>{{componentName}}</component>"
          values={{ machineName: activity.machine_name, componentName: activity.component_name }}
          components={{
            machine: <Text bold hover onClick={e => onMachineClick(e, activity.machine_id)} />,
            component: <Text bold />
          }}
        />
      </Text>
    ),
    description: () => (
      <DescriptionContainer>
        <Text>{i18n.t('activity.activityFormat.retrainHealthScoreModels', { defaultValue: 'Retrain the health score models for this asset.' })}</Text>
      </DescriptionContainer>
    ),
    icon: () => ''
  },
  // Any AHS feedback type this build has no entry for (the backend adds them over
  // time), so the feed shows a plain row instead of crashing the page.
  ahs_feedback: {
    title: activity => (
      <Text>
        <Trans
          i18nKey="activity.activityFormat.healthScoreFeedbackTitle"
          defaults="Health score feedback on <machine>{{machineName}}</machine> at <component>{{componentName}}</component>"
          values={{ machineName: activity.machine_name, componentName: activity.component_name }}
          components={{
            machine: <Text bold hover onClick={e => onMachineClick(e, activity.machine_id)} />,
            component: <Text bold />
          }}
        />
      </Text>
    ),
    description: activity => (
      <DescriptionContainer>
        <Text>{humanize(activity.feedback_type)}</Text>
      </DescriptionContainer>
    ),
    icon: () => ''
  },
  task: {
    title: activity => (
      <Text>
        <Trans
          i18nKey="activity.activityFormat.taskActivityTitle"
          values={{
            machineName: activity.machine_name,
            taskName: activity.name,
            userName: activity.user_name,
          }}
          components={{
            machine: <Text bold hover onClick={e => onMachineClick(e, activity.machine_id)} />,
            taskname: <Text bold />,
            username: <Text bold />,
          }}
        />
      </Text>
    ),
    icon: () => <Task />
  },
  repair: {
    title: activity => (
      <Text>
        <Trans
          i18nKey="activity.activityFormat.repairActivityTitle"
          values={{
            machineName: activity.machine_name,
            repairName: activity.name,
            userName: activity.user_name,
          }}
          components={{
            machine: <Text bold hover onClick={e => onMachineClick(e, activity.machine_id)} />,
            repairname: <Text bold />,
            username: <Text bold />,
          }}
        />
      </Text>
    ),
    icon: () => <Wrench />
  },
  note: {
    icon: () => <NoteSvgNew />
  }
};

export default activityFormat;
